/**
 * @fileoverview Mission definitions — the 4C/ID "learning tasks" component
 *
 * Five whole-task missions ordered from simple to complex with fading
 * scaffolding (4C/ID task classes):
 *
 *   M1  Worked example / observation  — maximum support, no controls
 *   M2  Completion task               — balance one floor, targets shown
 *   M3  Whole task with support       — balance the building, targets shown
 *   M4  Whole task, low support       — optimize heating curve & pump for COP
 *   M5  Transfer task, no support     — service call, targets hidden
 *
 * Each mission is pure data + pure functions:
 *   setup(buildScenario helpers) → scenario  (params, presets, room patches)
 *   evaluate(snapshot)           → { criteria[], passed, stars }
 *
 * The snapshot is assembled by the engine each tick:
 *   { rooms, deviations, params, supplyTemp, cop, network, scores,
 *     timeSinceChange (sim seconds), questionState }
 *
 * @module HydraulicBalancingTesting/missions
 */

import { PRESET_MAX_INDEX, SCORING, HEAT_PUMP } from './constants';
import { autoBalancePresets, applyDesignCalculation } from './physics';

// ============================================================================
// EVALUATION HELPERS
// ============================================================================

const pct = (v) => `${(v * 100).toFixed(0)}%`;

/** Criterion: every listed room within tolerance of its design flow. */
const flowCriteria = (snapshot, roomIndices, tolerance) =>
  roomIndices.map((idx) => {
    const dev = snapshot.deviations[idx];
    const room = snapshot.rooms[idx];
    return {
      label: `${room.name} (R${room.id}) within ±${pct(tolerance)} of design flow`,
      pass: Math.abs(dev) <= tolerance,
      detail: `${dev >= 0 ? '+' : ''}${pct(dev)}`,
    };
  });

const maxAbsDev = (snapshot, roomIndices) =>
  Math.max(...roomIndices.map((i) => Math.abs(snapshot.deviations[i])));

const starsFromDev = (maxDev, t3, t2, t1) => {
  if (maxDev <= t3) return 3;
  if (maxDev <= t2) return 2;
  if (maxDev <= t1) return 1;
  return 0;
};

const ALL_ROOMS = [0, 1, 2, 3, 4, 5, 6, 7, 8];
const FLOOR0 = [0, 1, 2];

// ============================================================================
// MISSIONS
// ============================================================================

export const MISSIONS = [
  // --------------------------------------------------------------------------
  {
    id: 'm1',
    order: 1,
    title: 'Meet the Unbalanced System',
    tagline: 'Observe, measure, diagnose — no tools needed yet.',
    difficulty: 1,
    kind: 'observe',
    theoryRefs: ['why-balance', 'pressure-flow'],
    brief: [
      'A family has just moved into this 9-room house. The heat pump runs, but they complain: the Studio under the roof never gets warm, while the Kitchen is tropical. Nobody has ever balanced this system — every valve is fully open (preset "N").',
      'Your job in this first task is only to OBSERVE. Start the simulation, watch where the water goes, and answer the diagnostic questions.',
    ],
    steps: [
      'Press Run and let the simulation work for at least 30 simulated minutes.',
      'Open the "Flow deviation" overlay (toolbar) — it compares each room\'s actual flow with its design flow.',
      'Click on individual rooms to inspect flow, return temperature and valve data.',
      'Answer the three diagnostic questions below.',
    ],
    scaffold: {
      showTargets: true,
      presetsEditable: [],
      systemEditable: [],
      balancingToggle: false,
      defaultOverlay: 'deviation',
    },
    questions: [
      {
        id: 'q1',
        text: 'Which rooms receive MORE water than their design flow?',
        options: [
          'The rooms closest to the pump (ground floor, near the riser)',
          'The rooms furthest from the pump (top floor, far end)',
          'All rooms equally — the pump pushes the same flow everywhere',
          'Only the bathroom, because it has the highest setpoint',
        ],
        correct: 0,
        explain:
          'Water is lazy: it takes the path of least resistance. The branches near the pump have short pipe runs and therefore little friction loss, so they swallow more than their share — they OVERFLOW.',
      },
      {
        id: 'q2',
        text: 'Why does the Studio (top floor, far corner) stay cold?',
        options: [
          'Its radiator is too small for the room',
          'The pressure left over after the long pipe run is too small, so its flow starves',
          'The heat pump cannot heat rooms on the top floor',
          'Cold air falls down from the roof into the radiator',
        ],
        correct: 1,
        explain:
          'Every metre of pipe and every tee eats differential pressure (Δp = r·V̇²). The Studio sits at the end of the longest run, so the least pressure — and the least flow — remains for it. Its radiator is fine; it is simply starved of water.',
      },
      {
        id: 'q3',
        text: 'What does the overflow in the near rooms do to the heat pump?',
        options: [
          'Nothing — more flow is always good for a heat pump',
          'The water rushes through the radiators barely cooled, so the return temperature rises and the COP drops',
          'It makes the heat pump produce hotter water automatically',
          'It cools the compressor and improves efficiency',
        ],
        correct: 1,
        explain:
          'Water racing through a radiator has no time to give off its heat: it comes back almost as hot as it left (low spread). A high return temperature forces a high condensing temperature — and every degree costs roughly 2–3% COP.',
      },
    ],
    setup: ({ defaultBuilding }) => ({
      params: {
        tOutdoor: -5,
        supplyMode: 'fixed',
        tSupplyFixed: 45,
        pumpHead: 40,
        balancingMode: false,
      },
      rooms: defaultBuilding().map((r) => ({ ...r, presetIndex: PRESET_MAX_INDEX })),
      running: true,
    }),
    evaluate: (snapshot) => {
      const qs = snapshot.questionState || {};
      const criteria = [
        {
          label: 'All three diagnostic questions answered correctly',
          pass: ['q1', 'q2', 'q3'].every((id) => qs[id]?.correct),
          detail: `${['q1', 'q2', 'q3'].filter((id) => qs[id]?.correct).length}/3`,
        },
      ];
      const passed = criteria.every((c) => c.pass);
      const firstTry = ['q1', 'q2', 'q3'].filter((id) => qs[id]?.correct && qs[id]?.attempts === 1).length;
      return { criteria, passed, stars: passed ? Math.max(firstTry, 1) : 0 };
    },
  },

  // --------------------------------------------------------------------------
  {
    id: 'm2',
    order: 2,
    title: 'Balance the Ground Floor',
    tagline: 'Your first presets — three valves, three design flows.',
    difficulty: 2,
    kind: 'balance',
    theoryRefs: ['valves-kv', 'method-b'],
    brief: [
      'A colleague has already balanced floors 1 and 2 — only the ground floor is left. The system is in COMMISSIONING MODE: all thermostatic heads are removed, so every valve is fully open and you see the raw hydraulic flows.',
      'Set the preset position of the three ground-floor valves so each room receives its design flow (±10%). Remember: a LOWER preset number = smaller Kv = more restriction = less flow.',
    ],
    steps: [
      'Select a ground-floor room and compare its actual flow with the design flow.',
      'Overflowing room → reduce the preset position. Starving room → increase it.',
      'Watch the other rooms while you adjust: every change shifts flow to ALL other branches.',
      'Iterate until all three ground-floor rooms show ±10% or better.',
    ],
    scaffold: {
      showTargets: true,
      presetsEditable: FLOOR0,
      systemEditable: [],
      balancingToggle: false,
      defaultOverlay: 'deviation',
    },
    setup: ({ defaultBuilding, balancedPresets }) => {
      const rooms = defaultBuilding();
      const presets = balancedPresets(rooms, 40);
      return {
        params: {
          tOutdoor: -5,
          supplyMode: 'fixed',
          tSupplyFixed: 45,
          pumpHead: 40,
          balancingMode: true,
        },
        rooms: rooms.map((r, i) => ({
          ...r,
          presetIndex: FLOOR0.includes(i) ? PRESET_MAX_INDEX : presets[i],
        })),
        running: true,
      };
    },
    evaluate: (snapshot) => {
      const criteria = flowCriteria(snapshot, FLOOR0, SCORING.BALANCE_TOLERANCE);
      const passed = criteria.every((c) => c.pass);
      const stars = passed ? starsFromDev(maxAbsDev(snapshot, FLOOR0), 0.04, 0.07, 0.10) : 0;
      return { criteria, passed, stars };
    },
  },

  // --------------------------------------------------------------------------
  {
    id: 'm3',
    order: 3,
    title: 'Balance the Whole Building',
    tagline: 'Verfahren B, the real thing: nine valves, one network.',
    difficulty: 3,
    kind: 'balance',
    theoryRefs: ['method-b', 'valves-kv', 'pressure-flow'],
    brief: [
      'Now the full job. All nine presets are back at "N" — the building is as unbalanced as on day one. The design flows have been calculated for you from each room\'s heat load (that is the essence of "Verfahren B": load → flow → preset).',
      'Bring every room within ±10% of its design flow. Strategy tip from the field: start with the most-starved room (give it free flow), then throttle the overflowing rooms — work from the worst offender inwards.',
    ],
    steps: [
      'Check the flow-deviation overlay: blue = starving, red = overflowing.',
      'Leave the worst-starved room (usually top-far) at "N" — it sets the pace.',
      'Throttle the overflowing rooms step by step; re-check after every change.',
      'Fine-tune until all nine rooms are within ±10% (±5% earns three stars).',
    ],
    scaffold: {
      showTargets: true,
      presetsEditable: ALL_ROOMS,
      systemEditable: [],
      balancingToggle: false,
      defaultOverlay: 'deviation',
    },
    setup: ({ defaultBuilding }) => ({
      params: {
        tOutdoor: -5,
        supplyMode: 'fixed',
        tSupplyFixed: 45,
        pumpHead: 40,
        balancingMode: true,
      },
      rooms: defaultBuilding().map((r) => ({ ...r, presetIndex: PRESET_MAX_INDEX })),
      running: true,
    }),
    evaluate: (snapshot) => {
      const criteria = flowCriteria(snapshot, ALL_ROOMS, SCORING.BALANCE_TOLERANCE);
      const passed = criteria.every((c) => c.pass);
      const stars = passed ? starsFromDev(maxAbsDev(snapshot, ALL_ROOMS), 0.05, 0.08, 0.10) : 0;
      return { criteria, passed, stars };
    },
  },

  // --------------------------------------------------------------------------
  {
    id: 'm4',
    order: 4,
    title: 'Optimize for the Heat Pump',
    tagline: 'The payoff: lower the curve, harvest the COP.',
    difficulty: 4,
    kind: 'optimize',
    theoryRefs: ['heat-pumps', 'radiators-dt'],
    brief: [
      'The building is balanced — now collect the reward. Because every radiator gets exactly its design flow, no room needs "too hot" water any more. The thermostatic heads are mounted again and the system runs normally.',
      'Lower the weather-compensated heating curve and the pump head as far as comfort allows. Every degree less supply temperature is worth ~2–3% COP; every watt of pump power you save is free money.',
      'The success check needs a STABLE result: criteria must hold after the building has settled for 2 simulated hours since your last change. Use the 20× speed.',
    ],
    steps: [
      'Switch to a higher simulation speed and let the building reach steady state.',
      'Reduce the heating-curve slope step by step; watch the coldest room.',
      'Reduce the pump head — but keep an eye on the heat-pump minimum flow.',
      'When all rooms hold their setpoint (±1 K) with COP ≥ 3.2, you have passed.',
    ],
    scaffold: {
      showTargets: true,
      presetsEditable: [],
      systemEditable: ['curveSlope', 'curveOffset', 'pumpHead'],
      balancingToggle: false,
      defaultOverlay: 'temperature',
    },
    setup: ({ defaultBuilding, balancedPresets }) => {
      const rooms = defaultBuilding();
      const presets = balancedPresets(rooms, 40);
      return {
        params: {
          tOutdoor: -5,
          supplyMode: 'curve',
          curveSlope: HEAT_PUMP.CURVE_SLOPE_DEFAULT,
          curveOffset: HEAT_PUMP.CURVE_OFFSET_DEFAULT,
          pumpHead: 40,
          balancingMode: false,
        },
        rooms: rooms.map((r, i) => ({ ...r, presetIndex: presets[i], T: r.Tset })),
        running: true,
      };
    },
    evaluate: (snapshot) => {
      const settled = snapshot.timeSinceChange >= 2 * 3600;
      const comfortable = snapshot.rooms.every((r) => Math.abs(r.T - r.Tset) <= SCORING.COMFORT_TOLERANCE);
      const cop = snapshot.cop;
      const minFlowOk = !snapshot.alarms.minFlow;
      const criteria = [
        {
          label: 'Stable for 2 simulated hours since the last change',
          pass: settled,
          detail: `${(snapshot.timeSinceChange / 3600).toFixed(1)} h`,
        },
        {
          label: 'Every room holds its setpoint (±1.0 K)',
          pass: comfortable,
          detail: `worst ${Math.max(...snapshot.rooms.map((r) => Math.abs(r.T - r.Tset))).toFixed(1)} K`,
        },
        { label: 'COP at least 3.2', pass: cop >= 3.2, detail: cop.toFixed(2) },
        { label: 'Heat-pump minimum flow respected', pass: minFlowOk, detail: minFlowOk ? 'OK' : 'ALARM' },
      ];
      const passed = criteria.every((c) => c.pass);
      let stars = 0;
      if (passed) {
        if (cop >= 3.40) stars = 3;
        else if (cop >= 3.30) stars = 2;
        else stars = 1;
      }
      return { criteria, passed, stars };
    },
  },

  // --------------------------------------------------------------------------
  {
    id: 'm5',
    order: 5,
    title: 'The Service Call',
    tagline: 'Transfer task: diagnose and rebalance — no safety net.',
    difficulty: 5,
    kind: 'transfer',
    theoryRefs: ['method-b'],
    brief: [
      'A customer calls: "Since the attic conversion, the heating has gone crazy — some rooms boil, others freeze." A previous installer has twisted the presets at random, and one renovated room now has a different heat load.',
      'This time there is NO deviation overlay and no target flows. You get what a real installer has: the measured flow of each branch (l/h), and each room\'s calculated heat load (W). Compute the design flows yourself — V̇ = Q / (ρ·c·ΔT) with the 5 K design spread — or practise first in the Drills tab.',
      'Bring every room within ±15% of its design flow.',
    ],
    steps: [
      'Switch on commissioning mode to see the raw hydraulic flows.',
      'For each room, convert its heat load into a design flow (the Drills tab teaches the 30-second method: l/h ≈ W / 5.8 at 5 K spread).',
      'Compare with the measured flows, find the misadjusted valves, and rebalance.',
      'All nine rooms within ±15% completes the job.',
    ],
    scaffold: {
      showTargets: false,
      presetsEditable: ALL_ROOMS,
      systemEditable: [],
      balancingToggle: true,
      defaultOverlay: 'none',
    },
    setup: ({ defaultBuilding, rng }) => {
      const rooms = defaultBuilding();
      // The attic conversion: studio lost insulation quality, bigger load.
      const renovated = rooms.map((r) =>
        r.id === 9 ? { ...r, Uout: r.Uout + 0.5 } : r
      );
      const recalculated = applyDesignCalculation(renovated, { resizeRadiators: false });
      return {
        params: {
          tOutdoor: -5,
          supplyMode: 'fixed',
          tSupplyFixed: 45,
          pumpHead: 40,
          balancingMode: true,
        },
        rooms: recalculated.map((r) => ({
          ...r,
          // previous installer's random twiddling
          presetIndex: 4 + Math.floor(rng() * (PRESET_MAX_INDEX - 3)),
        })),
        running: true,
      };
    },
    evaluate: (snapshot) => {
      const criteria = [
        {
          label: 'All nine rooms within ±15% of design flow',
          pass: snapshot.deviations.every((d) => Math.abs(d) <= SCORING.BALANCE_TOLERANCE_LOOSE),
          detail: `worst ${pct(Math.max(...snapshot.deviations.map(Math.abs)))}`,
        },
      ];
      const passed = criteria.every((c) => c.pass);
      const stars = passed
        ? starsFromDev(maxAbsDev(snapshot, ALL_ROOMS), 0.08, 0.12, 0.15)
        : 0;
      return { criteria, passed, stars };
    },
  },
];

/** Mission lookup by id. */
export const getMission = (id) => MISSIONS.find((m) => m.id === id) || null;

/**
 * Builds the helper bag passed to mission setup functions.
 * @param {Function} defaultBuilding - () => fresh annotated room array
 */
export const buildScenarioHelpers = (defaultBuilding) => ({
  defaultBuilding,
  balancedPresets: (rooms, pumpHead) => autoBalancePresets(rooms, pumpHead),
  rng: Math.random,
});
